import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiCopy, FiInbox, FiPlus, FiSend, FiTrash2, FiXCircle } from 'react-icons/fi';
import { push, ref, remove, set, update } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import {
  askCareerCoachStream,
  generateCvBuildEditSession,
  startCvBuildEditSession,
  turnCvBuildEditSession,
} from '../careerCoachApi';
import MarkdownResponse from '../components/MarkdownResponse';
import UpgradeDialog from '../upgrade/UpgradeDialog';
import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import { hasPaidAccess } from '../entitlements';
import { coachResetDate, readUpgradeDraft, saveUpgradeDraft, clearUpgradeDraft } from '../upgrade/upgradeIntent';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useLanguage } from '../contexts/LanguageContext';

const MOBILE_BREAKPOINT = 760;
const STREAM_PERSIST_INTERVAL_MS = 750;
const DEFAULT_CHAT_TITLE = 'New Career Coaching Conversation';
const TITLE_STOP_WORDS = new Set([
  'a', 'an', 'the', 'to', 'for', 'of', 'in', 'on', 'at', 'is', 'are', 'am', 'i', 'me', 'my', 'do', 'does',
  'did', 'can', 'could', 'would', 'should', 'what', 'how', 'why', 'when', 'where', 'which', 'about', 'please',
]);
const TITLE_FILLER_WORDS = ['Career', 'Coaching', 'Conversation', 'Notes'];
const TITLE_ACRONYMS = new Set(['ai', 'api', 'cv', 'ml', 'qa', 'ui', 'ux']);
const CV_SESSION_ACTIVE_STATUSES = new Set(['mode_choice', 'collecting', 'draft_ready', 'previewed', 'generating']);
const CV_DOCUMENT_PATTERN = /\b(cv|resume|curriculum vitae)\b/i;
const CV_BUILD_ACTION_PATTERN = /\b(add|build|create|edit|generate|improve|make|replace|review|rewrite|save|tailor|update|upload|write)\b/i;

function formatTime(timestamp, language = 'en') {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

function formatDateLabel(timestamp, language = 'en') {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function isMobileViewport() {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
}

function normalizeInputControl(value) {
  if (!value || typeof value !== 'object') return null;
  const options = Array.isArray(value.options)
    ? value.options
      .map((option) => ({
        value: String(option?.value || '').trim(),
        label: String(option?.label || option?.value || '').trim(),
      }))
      .filter((option) => option.value && option.label)
    : [];
  if (!options.length) return null;
  return {
    ...value,
    id: String(value.id || '').trim(),
    type: value.type || 'single_choice',
    textAllowed: value.textAllowed === true,
    options,
  };
}

function normalizeCvDraftTabs(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((tab, index) => ({
      id: String(tab?.id || `section-${index + 1}`).trim(),
      label: String(tab?.label || `Section ${index + 1}`).trim(),
      content: String(tab?.content || '').trim(),
    }))
    .filter((tab) => tab.id && tab.label && tab.content)
    .slice(0, 10);
}

function normalizeCvBuildSession(value) {
  if (!value || typeof value !== 'object') return null;
  const turns = Array.isArray(value.turns) ? value.turns : [];
  return {
    ...value,
    status: value.status || 'collecting',
    turns,
    currentInputControl: normalizeInputControl(value.currentInputControl),
    draftTabs: normalizeCvDraftTabs(value.draftTabs),
  };
}

function isCvSessionActive(session) {
  return Boolean(session && CV_SESSION_ACTIVE_STATUSES.has(session.status));
}

function shouldStartCvBuildSession(question) {
  const value = String(question || '');
  return CV_DOCUMENT_PATTERN.test(value) && CV_BUILD_ACTION_PATTERN.test(value);
}

function formatTitleWord(word) {
  const clean = String(word || '').toLowerCase();
  if (TITLE_ACRONYMS.has(clean)) return clean.toUpperCase();
  return `${clean.charAt(0).toUpperCase()}${clean.slice(1)}`;
}

function buildConversationTitle(value, fallback = DEFAULT_CHAT_TITLE, language = 'en') {
  const words = String(value || '')
    .replace(/['"]/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean)
    .filter((word) => !TITLE_STOP_WORDS.has(word.toLowerCase()))
    .slice(0, 6)
    .map((word) => (language === 'ar' ? word : formatTitleWord(word)));

  if (language !== 'ar') {
    TITLE_FILLER_WORDS.forEach((word) => {
      if (words.length < 4 && !words.includes(word)) words.push(word);
    });
  }

  return words.length ? words.slice(0, 6).join(' ') : fallback;
}

function normalizeChat([id, value], language = 'en', defaultTitle = DEFAULT_CHAT_TITLE, cvSessionTitle = 'CV Build Edit Session') {
  const rawTitle = value.title && value.title !== 'New AMA chat' ? value.title : '';
  const storedTitle = rawTitle === 'CV Build Edit Session' ? cvSessionTitle : rawTitle;
  return {
    id,
    title: buildConversationTitle(storedTitle || value.lastMessage || defaultTitle, defaultTitle, language),
    createdAt: value.createdAt || 0,
    updatedAt: value.updatedAt || value.createdAt || 0,
    lastMessage: value.lastMessage || '',
    status: value.status || 'ready',
    cvBuildSession: normalizeCvBuildSession(value.cvBuildSession),
  };
}

function normalizeMessage([id, value]) {
  return {
    id,
    role: value.role || 'assistant',
    content: value.content || '',
    timestamp: value.timestamp || 0,
    sources: Array.isArray(value.sources) ? value.sources : [],
    status: value.status || '',
    inputControl: normalizeInputControl(value.inputControl),
    cvDraftTabs: normalizeCvDraftTabs(value.cvDraftTabs),
  };
}

function renderMessageContent(message) {
  const parts = message.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\/[^\s]+$/.test(part)) {
      return (
        <a key={`${part}-${index}`} href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function sourceLabel(source) {
  return [source.domain, source.publication_date].filter(Boolean).join(' · ');
}

function truncateTitle(value, limit = 42) {
  const clean = String(value || '').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).trim()}...`;
}

function SourceList({ sources, translate }) {
  if (!sources?.length) return null;
  return (
    <div className="js-ama-message-sources" aria-label={translate('sources')} title={sources.map((source) => sourceLabel(source)).filter(Boolean).join('\n')}>
      {sources.map((source) => (
        <a key={`${source.index}-${source.url}`} href={source.url} target="_blank" rel="noreferrer">
          <strong>[{source.index}]</strong>
          <span>{truncateTitle(source.title)}</span>
        </a>
      ))}
    </div>
  );
}

function CvInputControl({ control, onChoose, disabled, translate }) {
  if (!control?.options?.length) return null;
  return (
    <div className="js-ama-message-options" aria-label={translate('cvOptions')}>
      {control.options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChoose(option)}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function CvDraftTabs({ tabs, translate }) {
  const safeTabs = normalizeCvDraftTabs(tabs);
  const [activeId, setActiveId] = useState(safeTabs[0]?.id || '');
  const activeTab = safeTabs.find((tab) => tab.id === activeId) || safeTabs[0];

  useEffect(() => {
    if (!safeTabs.length) return;
    if (!safeTabs.some((tab) => tab.id === activeId)) {
      setActiveId(safeTabs[0].id);
    }
  }, [activeId, safeTabs]);

  if (!safeTabs.length) return null;

  return (
    <div className="js-cv-draft-tabs">
      <div className="js-cv-draft-tabs__list" role="tablist" aria-label={translate('cvSections')}>
        {safeTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === activeTab.id}
            className={tab.id === activeTab.id ? 'is-active' : ''}
            onClick={() => setActiveId(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="js-cv-draft-tabs__panel" role="tabpanel">
        <MarkdownResponse content={`## ${activeTab.label}\n${activeTab.content}`} />
      </div>
    </div>
  );
}

function AskAnythingScreen({ user, onProfileSaved }) {
  const { entitlements, refresh: refreshEntitlements } = useEntitlements();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const ta = (key) => t(`askAnythingPage.${key}`);
  const interpolate = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const defaultTitle = ta('defaultTitle');
  const cvSessionTitle = ta('cvSessionTitle');
  const chatsPath = user?.uid ? `people/${user.uid}/amaChats` : '';
  const cachedChatsRaw = chatsPath ? getCachedFirebaseValue(chatsPath) : undefined;
  const initialChats = Object.entries(cachedChatsRaw || {})
    .map((entry) => normalizeChat(entry, language, defaultTitle, cvSessionTitle))
    .sort((left, right) => right.updatedAt - left.updatedAt);
  const initialMobile = isMobileViewport();
  const [savedQuestion] = useState(() => readUpgradeDraft(user?.uid, 'coach'));
  const initialSelectedId = savedQuestion?.chatId || (initialMobile ? '' : initialChats[0]?.id || '');
  const initialMessagesPath = user?.uid && initialSelectedId
    ? `people/${user.uid}/amaChats/${initialSelectedId}/messages`
    : '';
  const cachedMessagesRaw = initialMessagesPath ? getCachedFirebaseValue(initialMessagesPath) : undefined;
  const [chats, setChats] = useState(initialChats);
  const [messages, setMessages] = useState(() => Object.entries(cachedMessagesRaw || {})
    .map(normalizeMessage)
    .sort((left, right) => left.timestamp - right.timestamp));
  const [selectedId, setSelectedId] = useState(initialSelectedId);
  const [draft, setDraft] = useState(savedQuestion?.question || '');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const allowance = entitlements?.features?.career_coach_ama;
  const paid = hasPaidAccess(entitlements);
  const remaining = !paid && Number.isFinite(allowance?.remaining) ? Math.max(0, allowance.remaining) : null;
  const resetDate = coachResetDate(allowance?.month, language);
  const [loading, setLoading] = useState(Boolean(user?.uid) && cachedChatsRaw === undefined);
  const [messagesLoaded, setMessagesLoaded] = useState(!initialSelectedId || cachedMessagesRaw !== undefined);
  const [sending, setSending] = useState(false);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState('');
  const textareaRef = useRef(null);
  const isMobileRef = useRef(initialMobile);
  const prefillAppliedRef = useRef(false);
  const localStreamUpdatesRef = useRef({});

  useEffect(() => {
    if (prefillAppliedRef.current) return;
    const prefill = String(location.state?.prefill || '').trim();
    if (!prefill) return;
    prefillAppliedRef.current = true;
    setDraft(prefill);
  }, [location.state]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    let receivedFirstSnapshot = false;
    const chatsRef = ref(db, chatsPath);
    const unsubscribe = subscribeCachedValue(chatsRef, chatsPath, (snapshot) => {
      const payload = snapshot.val() || {};
      const nextChats = Object.entries(payload)
        .map((entry) => normalizeChat(entry, language, defaultTitle, cvSessionTitle))
        .sort((left, right) => right.updatedAt - left.updatedAt);
      setChats(nextChats);
      if (!isMobileRef.current) {
        setSelectedId((current) => {
          if (current && nextChats.some((chat) => chat.id === current)) return current;
          return nextChats[0]?.id || '';
        });
      }
      if (!receivedFirstSnapshot) {
        receivedFirstSnapshot = true;
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [user?.uid, chatsPath, language, defaultTitle, cvSessionTitle]);

  useEffect(() => {
    if (!user?.uid || !selectedId) {
      setMessages([]);
      setMessagesLoaded(true);
      return undefined;
    }
    const messagesPath = `people/${user.uid}/amaChats/${selectedId}/messages`;
    const cachedMessages = getCachedFirebaseValue(messagesPath);
    if (cachedMessages === undefined) {
      setMessages([]);
      setMessagesLoaded(false);
    }
    const messagesRef = ref(db, messagesPath);
    const unsubscribe = subscribeCachedValue(messagesRef, messagesPath, (snapshot) => {
      const payload = snapshot.val() || {};
      const nextMessages = Object.entries(payload)
        .map(normalizeMessage)
        .sort((left, right) => left.timestamp - right.timestamp);
      setMessages(nextMessages.map((message) => ({
        ...message,
        ...(localStreamUpdatesRef.current[message.id] || {}),
      })));
      setMessagesLoaded(true);
    });
    return unsubscribe;
  }, [user?.uid, selectedId]);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = '0px';
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 180)}px`;
  }, [draft]);

  const selectedChat = useMemo(
    () => chats.find((chat) => chat.id === selectedId),
    [chats, selectedId],
  );
  const activeCvBuildSession = isCvSessionActive(selectedChat?.cvBuildSession)
    ? selectedChat.cvBuildSession
    : null;
  const activeCvInputControl = normalizeInputControl(activeCvBuildSession?.currentInputControl);
  const headerTitle = selectedChat?.title || defaultTitle;
  const showStarter = messagesLoaded && messages.length === 0 && !sending;

  const createChat = async () => {
    const now = Date.now();
    const chatRef = push(ref(db, `people/${user.uid}/amaChats`));
    await set(chatRef, {
      title: defaultTitle,
      createdAt: now,
      updatedAt: now,
      lastMessage: ta('helpTitle'),
      status: 'ready',
    });
    setSelectedId(chatRef.key);
    setChatDrawerOpen(false);
    return chatRef.key;
  };

  const handleSelect = (chatId) => {
    setSelectedId(chatId);
    setChatDrawerOpen(false);
  };

  const handleDeleteChat = async (event, chatId) => {
    event.stopPropagation();
    if (!chatId || !user?.uid) return;
    const nextChatId = chats.find((chat) => chat.id !== chatId)?.id || '';
    await remove(ref(db, `people/${user.uid}/amaChats/${chatId}`));
    if (selectedId === chatId) {
      setSelectedId(nextChatId);
    }
  };

  const updateAssistantMessage = async (chatId, messageId, updates) => {
    await update(ref(db, `people/${user.uid}/amaChats/${chatId}/messages/${messageId}`), updates);
  };

  const appendChatMessage = async (chatId, role, content, extra = {}) => {
    const messageRef = push(ref(db, `people/${user.uid}/amaChats/${chatId}/messages`));
    await set(messageRef, {
      role,
      content,
      timestamp: Date.now(),
      ...extra,
    });
    return messageRef.key;
  };

  const appendAssistantResponseMessages = async (chatId, responseMessages, fallbackMessage, firstMessageId = '') => {
    const messagesToAppend = Array.isArray(responseMessages) && responseMessages.length
      ? responseMessages
      : [{ role: 'assistant', content: fallbackMessage || '' }];
    let lastContent = fallbackMessage || '';

    for (let index = 0; index < messagesToAppend.length; index += 1) {
      const message = messagesToAppend[index] || {};
      const content = message.content || fallbackMessage || '';
      lastContent = content || lastContent;
      const extra = {
        status: message.status || 'ready',
        ...(message.inputControl ? { inputControl: message.inputControl } : {}),
        ...(message.cvDraftTabs ? { cvDraftTabs: message.cvDraftTabs } : {}),
      };
      if (index === 0 && firstMessageId) {
        await updateAssistantMessage(chatId, firstMessageId, {
          content,
          ...extra,
        });
      } else {
        await appendChatMessage(chatId, message.role || 'assistant', content, extra);
      }
    }

    return lastContent;
  };

  const persistCvBuildSession = async (chatId, session, lastMessage, status = 'ready') => {
    await update(ref(db, `people/${user.uid}/amaChats/${chatId}`), {
      title: cvSessionTitle,
      updatedAt: Date.now(),
      lastMessage,
      status,
      cvBuildSession: session,
    });
  };

  const generateAndSaveCvSession = async (chatId, session, loadingMessageId = '') => {
    const generatingSession = {
      ...session,
      status: 'generating',
      updatedAt: Date.now(),
    };
    const thinkingMessageId = loadingMessageId
      || await appendChatMessage(chatId, 'assistant', ta('cvGenerating'), { status: 'loading' });
    await persistCvBuildSession(chatId, generatingSession, ta('cvCreatingSaving'), 'loading');
    const response = await generateCvBuildEditSession({ user, session: generatingSession });
    if (onProfileSaved) {
      await onProfileSaved({
        cv: { Link: response.downloadUrl },
        cvUrl: response.downloadUrl,
        cvMetadata: response.cvMetadata,
        ...(response.profileUpdates || {}),
      });
    }
    const savedSession = {
      ...generatingSession,
      status: 'saved',
      currentInputControl: null,
      savedAt: response.generatedAt || Date.now(),
      storagePath: response.storagePath,
      downloadUrl: response.downloadUrl,
      updatedAt: Date.now(),
    };
    const message = interpolate(ta('cvSaved'), { url: response.downloadUrl });
    await updateAssistantMessage(chatId, thinkingMessageId, {
      content: message,
      status: 'ready',
    });
    await persistCvBuildSession(chatId, savedSession, message);
  };

  const sendCvBuildSessionTurn = async (rawQuestion, existingSession = null, choiceValue = '') => {
    const question = String(rawQuestion || '').trim();
    if (!question || !user?.uid || sending) return;

    setDraft('');
    setSending(true);

    const chatId = selectedId || await createChat();
    await appendChatMessage(chatId, 'user', question);
    await update(ref(db, `people/${user.uid}/amaChats/${chatId}`), {
      title: cvSessionTitle,
      updatedAt: Date.now(),
      lastMessage: question,
      status: 'loading',
    });

    try {
      if (!existingSession) {
        const loadingMessageId = await appendChatMessage(chatId, 'assistant', ta('cvReading'), { status: 'loading' });
        const response = await startCvBuildEditSession({ user });
        const nextSession = normalizeCvBuildSession(response.session);
        const message = await appendAssistantResponseMessages(
          chatId,
          response.messages,
          response.message,
          loadingMessageId,
        );
        await persistCvBuildSession(chatId, nextSession, message);
        return;
      }

      const session = normalizeCvBuildSession(existingSession);
      const loadingText = choiceValue === 'generate_save'
        ? ta('cvGenerating')
        : ta('cvThinking');
      const loadingMessageId = await appendChatMessage(chatId, 'assistant', loadingText, { status: 'loading' });

      if (choiceValue === 'generate_save') {
        await generateAndSaveCvSession(chatId, session, loadingMessageId);
        return;
      }

      const response = await turnCvBuildEditSession({
        user,
        session,
        answer: question,
        choice: choiceValue || undefined,
      });
      const nextSession = normalizeCvBuildSession(response.session);
      const message = await appendAssistantResponseMessages(
        chatId,
        response.messages,
        response.message,
        loadingMessageId,
      );
      await persistCvBuildSession(chatId, nextSession, message);
    } catch (error) {
      const message = language === 'ar' ? ta('cvTemporaryError') : error.message || ta('cvTemporaryError');
      await appendChatMessage(chatId, 'assistant', message, { status: 'error' });
      await update(ref(db, `people/${user.uid}/amaChats/${chatId}`), {
        updatedAt: Date.now(),
        lastMessage: message,
        status: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const sendQuestion = async (rawQuestion) => {
    const question = String(rawQuestion || '').trim();
    if (!question || !user?.uid || sending) return;

    if (activeCvBuildSession) {
      await sendCvBuildSessionTurn(question, activeCvBuildSession);
      return;
    }

    if (shouldStartCvBuildSession(question)) {
      await sendCvBuildSessionTurn(question, null);
      return;
    }

    if (remaining === 0) { setDraft(question); setShowUpgrade(true); return; }
    clearUpgradeDraft(user?.uid, 'coach');
    setDraft('');
    setSending(true);

    const chatId = selectedId || await createChat();
    const now = Date.now();
    const chatTitle = selectedChat?.title && ![DEFAULT_CHAT_TITLE, defaultTitle].includes(selectedChat.title)
      ? selectedChat.title
      : buildConversationTitle(question, defaultTitle, language);

    const userMessageRef = push(ref(db, `people/${user.uid}/amaChats/${chatId}/messages`));
    const assistantMessageRef = push(ref(db, `people/${user.uid}/amaChats/${chatId}/messages`));
    const chatPath = `people/${user.uid}/amaChats/${chatId}`;

    await update(ref(db), {
      [`${chatPath}/messages/${userMessageRef.key}`]: {
        role: 'user',
        content: question,
        timestamp: now,
      },
      [`${chatPath}/messages/${assistantMessageRef.key}`]: {
        role: 'assistant',
        content: ta('searching'),
        timestamp: now + 1,
        sources: [],
        status: 'loading',
      },
      [`${chatPath}/title`]: chatTitle,
      [`${chatPath}/updatedAt`]: now,
      [`${chatPath}/lastMessage`]: question,
      [`${chatPath}/status`]: 'loading',
    });

    const history = messages
      .filter((message) => message.content && message.status !== 'loading')
      .map((message) => ({ role: message.role, content: message.content }));
    const collectedSources = [];
    let streamedAnswer = '';
    let persistTimer = null;
    let persistenceChain = Promise.resolve();
    let latestAssistantUpdate = {
      content: ta('searching'),
      sources: [],
      status: 'loading',
    };

    const applyAssistantUpdateLocally = (updates) => {
      latestAssistantUpdate = { ...latestAssistantUpdate, ...updates };
      localStreamUpdatesRef.current[assistantMessageRef.key] = latestAssistantUpdate;
      setMessages((current) => {
        const index = current.findIndex((message) => message.id === assistantMessageRef.key);
        if (index === -1) return current;
        const next = [...current];
        next[index] = { ...next[index], ...latestAssistantUpdate };
        return next;
      });
    };

    const queueAssistantPersistence = () => {
      const snapshot = { ...latestAssistantUpdate };
      persistenceChain = persistenceChain
        .catch(() => {})
        .then(() => updateAssistantMessage(chatId, assistantMessageRef.key, snapshot));
      return persistenceChain;
    };

    const scheduleAssistantPersistence = () => {
      if (persistTimer) return;
      persistTimer = window.setTimeout(() => {
        persistTimer = null;
        void queueAssistantPersistence();
      }, STREAM_PERSIST_INTERVAL_MS);
    };

    const stageAssistantUpdate = (updates) => {
      applyAssistantUpdateLocally(updates);
      scheduleAssistantPersistence();
    };

    const flushAssistantUpdate = async (updates = null) => {
      if (updates) applyAssistantUpdateLocally(updates);
      if (persistTimer) {
        window.clearTimeout(persistTimer);
        persistTimer = null;
      }
      await queueAssistantPersistence();
    };

    try {
      await askCareerCoachStream({
        user,
        question,
        history,
        onEvent: async (streamEvent) => {
          if (streamEvent.type === 'status') {
            stageAssistantUpdate({
              content: streamEvent.message || ta('searching'),
              status: 'loading',
            });
            return;
          }
          if (streamEvent.type === 'source' && streamEvent.source) {
            collectedSources.push(streamEvent.source);
            stageAssistantUpdate({
              sources: [...collectedSources],
              status: 'loading',
            });
            return;
          }
          if (streamEvent.type === 'answer_delta') {
            streamedAnswer += streamEvent.delta || '';
            stageAssistantUpdate({
              content: streamedAnswer || ta('brewing'),
              sources: [...collectedSources],
              status: 'streaming',
            });
            return;
          }
          if (streamEvent.type === 'answer') {
            const answer = streamEvent.answer || streamedAnswer || ta('noAnswer');
            await flushAssistantUpdate({
              content: answer,
              sources: [...collectedSources],
              status: 'ready',
            });
            await update(ref(db, `people/${user.uid}/amaChats/${chatId}`), {
              updatedAt: Date.now(),
              lastMessage: answer,
              status: 'ready',
            });
            delete localStreamUpdatesRef.current[assistantMessageRef.key];
            void refreshEntitlements();
            return;
          }
          if (streamEvent.type === 'blocked' || streamEvent.type === 'error') {
            const notice = streamEvent.message || ta('blocked');
            await flushAssistantUpdate({
              content: notice,
              status: streamEvent.type,
            });
            await update(ref(db, `people/${user.uid}/amaChats/${chatId}`), {
              updatedAt: Date.now(),
              lastMessage: notice,
              status: streamEvent.type,
            });
            delete localStreamUpdatesRef.current[assistantMessageRef.key];
          }
        },
      });
    } catch (error) {
      setDraft(question);
      if (error.code === 'monthly_limit_reached') { setShowUpgrade(true); void refreshEntitlements({ force: true }); }
      const message = error.message || ta('temporaryError');
      await flushAssistantUpdate({
        content: message,
        status: 'error',
      });
      await update(ref(db, `people/${user.uid}/amaChats/${chatId}`), {
        updatedAt: Date.now(),
        lastMessage: message,
        status: 'error',
      });
      delete localStreamUpdatesRef.current[assistantMessageRef.key];
    } finally {
      if (persistTimer) window.clearTimeout(persistTimer);
      setSending(false);
    }
  };

  const handleCvInputChoice = async (option) => {
    if (!option?.value || !activeCvBuildSession) return;
    await sendCvBuildSessionTurn(option.label || option.value, activeCvBuildSession, option.value);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    await sendQuestion(draft);
  };

  const handleDraftKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend(event);
    }
  };

  const handleCopyAnswer = async (message) => {
    const text = String(message?.content || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(message.id);
      window.setTimeout(() => {
        setCopiedMessageId((current) => (current === message.id ? '' : current));
      }, 1400);
    } catch {
      setCopiedMessageId('');
    }
  };

  if (loading) {
    return (
      <section className="js-inbox-loading" aria-label={ta('loadingChats')}>
        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        <p>{ta('loading')}</p>
      </section>
    );
  }

  return (
    <section className="js-ama-focus" aria-label={ta('label')}>
      <header className="js-ama-focus__head">
        <button type="button" className="js-ama-focus__back" aria-label={ta('back')} onClick={() => navigate('/career-coach')}>
          <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
          <span>{ta('back')}</span>
        </button>
        <div className="js-ama-focus__title">
          <h1>{headerTitle === defaultTitle ? <><span className="js-desktop-only">{headerTitle}</span><span className="js-mobile-only">{t('navigation.items.careerCoach')}</span></> : headerTitle}</h1>
        </div>
        <div className="js-focus-plan-actions"><PlanControlSlot />
        <button
          type="button"
          className="js-ama-focus__threads-button"
          onClick={() => setChatDrawerOpen((current) => !current)}
          aria-expanded={chatDrawerOpen}
          aria-label={ta('openChats')}
        >
          <FiInbox aria-hidden="true" />
        </button></div>
      </header>

      {chatDrawerOpen && (
        <aside className="js-ama-thread-popover" aria-label={ta('previousChats')}>
          <div className="js-ama-thread-popover__head">
            <strong>{ta('chats')}</strong>
            <button type="button" onClick={() => setChatDrawerOpen(false)} aria-label={ta('closeChats')}>
              <FiXCircle aria-hidden="true" />
            </button>
          </div>
          <button type="button" className="js-inbox-thread js-ama-new-thread" onClick={createChat}>
            <span className="js-inbox-thread__top">
              <strong>{ta('newChat')}</strong>
              <FiPlus aria-hidden="true" />
            </span>
            <span className="js-inbox-thread__role">{ta('startFresh')}</span>
            <span className="js-inbox-thread__preview">{ta('newChatDescription')}</span>
          </button>
          <div className="js-inbox__threads">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className={`js-ama-thread-row ${chat.id === selectedId ? 'is-selected' : ''}`}
              >
                <button
                  type="button"
                  className={`js-inbox-thread ${chat.id === selectedId ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(chat.id)}
                >
                  <span className="js-inbox-thread__top">
                    <strong>{chat.title}</strong>
                    <span>{formatTime(chat.updatedAt, language)}</span>
                  </span>
                  <span className="js-inbox-thread__role">{ta('coach')}</span>
                  <span className="js-inbox-thread__preview">
                    {chat.status === 'loading' && <i aria-label={ta('answerLoading')} />}
                    {chat.lastMessage || ta('noMessages')}
                  </span>
                </button>
                <button
                  type="button"
                  className="js-ama-thread-delete"
                  onClick={(event) => handleDeleteChat(event, chat.id)}
                  aria-label={interpolate(ta('deleteChat'), { title: chat.title })}
                  title={ta('delete')}
                >
                  <FiTrash2 aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        </aside>
      )}

      <div className="js-chat-messages js-ama-focus__messages">
        {showStarter && (
          <div className="js-ama-starter">
            <h2>{ta('helpTitle')}</h2>
            <div className="js-ama-starter__questions" aria-label={ta('suggested')}>
              {ta('suggestions').map((question) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => sendQuestion(question)}
                  disabled={sending}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((message, index) => {
          const outgoing = message.role === 'user';
          const previousMessage = messages[index - 1];
          const currentDate = formatDateLabel(message.timestamp, language);
          const previousDate = previousMessage ? formatDateLabel(previousMessage.timestamp, language) : '';
          const showDateDivider = currentDate !== previousDate;
          const messageControl = normalizeInputControl(message.inputControl);
          const controlIsActive = Boolean(
            !outgoing
            && messageControl
            && activeCvInputControl
            && messageControl.id === activeCvInputControl.id,
          );

          return (
            <React.Fragment key={message.id}>
              {showDateDivider && <div className="js-chat-date">{currentDate}</div>}
              <article className={`js-chat-message ${outgoing ? 'is-outgoing' : 'is-agent'} ${message.status === 'loading' ? 'is-loading' : ''}`}>
                {outgoing ? (
                  <p>{renderMessageContent(message.content)}</p>
                ) : (
                  <>
                    <MarkdownResponse content={message.content} />
                    <CvDraftTabs tabs={message.cvDraftTabs} translate={ta} />
                    {controlIsActive && (
                      <CvInputControl
                        control={activeCvInputControl}
                        onChoose={handleCvInputChoice}
                        disabled={sending}
                        translate={ta}
                      />
                    )}
                    <SourceList sources={message.sources} translate={ta} />
                    <span className="js-ama-answer-footer">
                      <button
                        type="button"
                        className="js-ama-copy-answer"
                        onClick={() => handleCopyAnswer(message)}
                        aria-label={ta('copy')}
                      >
                        {copiedMessageId === message.id ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                      </button>
                      <time>{formatTime(message.timestamp, language)}</time>
                    </span>
                  </>
                )}
                {outgoing && <time>{formatTime(message.timestamp, language)}</time>}
              </article>
            </React.Fragment>
          );
        })}
      </div>

      {activeCvInputControl && !activeCvInputControl.textAllowed ? (
        <div className="js-chat-compose js-ama-focus__compose js-ama-choice-compose" aria-label={ta('chooseCv')}>
          <span>{ta('chooseAbove')}</span>
        </div>
      ) : (
        <form className="js-chat-compose js-ama-focus__compose" onSubmit={handleSend}>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleDraftKeyDown}
            placeholder={ta('placeholder')}
            aria-label={ta('question')}
            disabled={sending}
          />
          <button type="submit" aria-label={ta('send')} disabled={sending || !draft.trim()}>
            <FiSend aria-hidden="true" />
          </button>
          {remaining !== null && !activeCvBuildSession && <div className="js-coach-allowance" role="status">
            <span>{language === 'ar' ? `${remaining} إجابات متبقية` : `${remaining} ${remaining === 1 ? 'answer' : 'answers'} left`}{resetDate ? (language === 'ar' ? ` · تتجدد في ${resetDate}` : ` · Resets ${resetDate}`) : ''}</span>
            {remaining === 0 && <button type="button" onClick={() => setShowUpgrade(true)}>{language === 'ar' ? 'المتابعة مع Pro' : 'Continue with Pro'}</button>}
          </div>}
        </form>
      )}
      {showUpgrade && !paid && <UpgradeDialog user={user} context="career_coach" onClose={() => setShowUpgrade(false)} onBeforeCheckout={() => saveUpgradeDraft(user?.uid, 'coach', { question: draft, chatId: selectedId })} destination={{ path: '/ask-anything' }} />}
    </section>
  );
}

export default AskAnythingScreen;
